import React, {
  ReactElement, useCallback, useEffect, useState,
} from 'react';
import {
  Col, Text, Loader, Row, usePageNumberFromUrl, usePerPageValueFromUrl, useSortValueFromUrl,
} from 'idsk-react-components';
import {
  MobileFilterModal,
  Pagination,
  SearchInputButton,
  useResponsiveContext,
} from 'slovlex-idsk-react-components';
import { useSearchParams } from 'react-router-dom';
import { PravoEuFilter } from '../../model/PravoEuFilter';
import SidePanel from './component/SidePanel';
import { SafeParameterSetter } from '../../types/SafeParameterSetter';
import FilterTagsComponent from './component/FilterTagsComponent';
import { defaultPerPageValue, perPageDataArray } from '../../constants/PaginationPerPageConstants';
import usePravoEuFilteredList from './hooks/usePravoEuFilteredList';
import PravoEuListComponent from './component/PravoEuListComponent';
import getPaginationTotalLabel from '../../function/getPaginationTotalLabel';
import countActiveFilters from '../../function/countActiveFilters';
import getUpdatedSearchParams from './function/getUpdatedSearchParams';
import CiselnikPolozka from '../../model/CiselnikPolozka';
import './component/pravoEuListComponent.scss';
import emptyFilter from './emptyFilter';
import PravoEuListSort from './component/PravoEuListSort';
import MobileFilters from './component/MobileFilters';

interface PravoEuListProps {
  initFilter: PravoEuFilter
}

// dvojice od/do sa pouzivatelovi zobrazuju ako jeden panel a jeden filter tag,
// takze kazda z nich je jedno kriterium
const criteriaGroups: Array<Array<keyof PravoEuFilter>> = [
  ['datumUverejneniaOd', 'datumUverejneniaDo'],
  ['datumSchvaleniaOd', 'datumSchvaleniaDo'],
  ['datumUcinnostiOdOd', 'datumUcinnostiOdDo'],
  ['datumUcinnostiDoOd', 'datumUcinnostiDoDo'],
  ['datumUzavierkyOd', 'datumUzavierkyDo'],
  ['datumTranspozicieOd', 'datumTranspozicieDo'],
];

const PravoEuList = ({ initFilter }: PravoEuListProps): ReactElement => {
  const [firstLoad, setFirstLoad] = useState(true);
  const { page } = usePageNumberFromUrl('page');
  const { perPageValue } = usePerPageValueFromUrl('perPage', perPageDataArray, defaultPerPageValue);
  const [searchParams, setSearchParams] = useSearchParams();
  const { sort } = useSortValueFromUrl();
  const [filter, setFilter] = useState<PravoEuFilter>(initFilter);
  const [filterCopy, setFilterCopy] = useState<PravoEuFilter>(filter);
  const { isSmallScreen } = useResponsiveContext();
  const [isOpenModal, setIsOpenModal] = useState(false);

  const onUpdateField: SafeParameterSetter<PravoEuFilter> = (fieldName, value): void => {
    setFilter({
      ...filterCopy,
      [fieldName]: value,
    });
    setFilterCopy({
      ...filterCopy,
      [fieldName]: value,
    });
  };

  const onUpdateFieldCopy: SafeParameterSetter<PravoEuFilter> = (fieldName, value): void => {
    setFilterCopy(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const onRecordsPerPageChange = (value: number): void => {
    const updatedSearchParams = new URLSearchParams(searchParams.toString());
    updatedSearchParams.set('perPage', value.toString());
    // SLASLX-3400: replace, not push — see NavrhAktuEuList for full rationale.
    setSearchParams(updatedSearchParams.toString(), { replace: true });
  };

  const {
    pravoEuPage, status,
  } = usePravoEuFilteredList(page, perPageValue, sort, filter);

  useEffect(() => {
    if (Array.from(searchParams.values()).length === 0
      && JSON.stringify(filter) !== JSON.stringify(emptyFilter)) {
      setFilter(emptyFilter);
      setFilterCopy(emptyFilter);
      setFirstLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!firstLoad) {
      const urlParamsFromFilter = getUpdatedSearchParams(1, perPageValue, sort, filter);
      // SLASLX-3400: replace — URL reflects filter state, not user navigation.
      setSearchParams(urlParamsFromFilter, { replace: true });
    }
    setFirstLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  const handleCloseModal = ():void => {
    setIsOpenModal(false);
    window.parent.postMessage({
      type: 'iframe-to-parent-filter-modal-close',
    }, '*');
    setFilter(filterCopy);
  };

  const handleOpenModal = ():void => {
    setIsOpenModal(true);
    window.parent.postMessage({
      type: 'iframe-to-parent-filter-modal-open',
    }, '*');
    setFilter(filterCopy);
  };

  const updateFilterCopy = useCallback(async (): Promise<void> => {
    setFilter(filterCopy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filterCopy)]);

  useEffect(() => {
    const keyDownHandler = async (e): Promise<void> => {
      if (e.key === 'Enter' && e.target.tagName.toLowerCase() !== 'select') {
        e.preventDefault();
        await updateFilterCopy();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [updateFilterCopy]);

  const clearFilters = (): void => {
    setFilter(emptyFilter);
    setFilterCopy(emptyFilter);
  };

  // tlacidlo na vymazanie filtra sa podla zadania zobrazuje az pri dvoch a viac kriteriach
  const onClearFilters = countActiveFilters(filter, criteriaGroups) >= 2 ? clearFilters : null;

  return (
    <div className="pravo-eu-list-component">
      {isSmallScreen && (
        <SearchInputButton
          label="Hľadaný výraz"
          className="slovlex-idsk-side-panel-input-wrapper"
          searchText={filterCopy?.hladanyVyraz}
          setSearchText={(value: string) => onUpdateFieldCopy('hladanyVyraz', value)}
          onButtonSubmit={updateFilterCopy}
          onClearSearch={() => onUpdateFieldCopy('hladanyVyraz', '')}
        />
      )}
      <MobileFilterModal
        handleClose={handleCloseModal}
        handleOpen={handleOpenModal}
        isOpen={isOpenModal}
        numberOfResults={pravoEuPage?.totalElements || 0}
        filters={(
          <MobileFilters
            setFilter={setFilter}
            filterCopy={filterCopy}
            onUpdateFieldCopy={onUpdateFieldCopy}
            filter={filter}
            onUpdateField={onUpdateField}
          />
          )}
        perPageDataArray={perPageDataArray}
        onPerPageChange={onRecordsPerPageChange}
        perPageValue={perPageValue}
        clearFilters={onClearFilters}
        filterTags={(
          <FilterTagsComponent
            filter={filter}
            onUpdateField={onUpdateField}
          />
          )}
      />
      {!isOpenModal && (
        <Row className="pravo-eu-list-component__results-row">
          {!isSmallScreen && (
          <Col className="side-panel-column" width="one-quarter">
            <SidePanel
              filter={filter}
              setFilter={setFilter}
              onUpdateField={onUpdateField}
              filterCopy={filterCopy}
              onUpdateFieldCopy={onUpdateFieldCopy}
            />
          </Col>
          )}
          <Col width="three-quarters">
            <FilterTagsComponent
              filter={filter}
              onUpdateField={onUpdateField}
              onClearFilters={onClearFilters}
            />
            <Loader loading={status.loading}>
              <>
                {isSmallScreen && (
                  <Row className="pravo-eu-mobile-sort-select-row">
                    <Col>
                      <PravoEuListSort />
                    </Col>
                  </Row>
                )}
                <Row className="pagination-label-sort-row">
                  <Col
                    width="full"
                    widthDesktop="one-third"
                    className={isSmallScreen && 'pagination-label-mobile'}
                  >
                    <Text className="pagination-label">{getPaginationTotalLabel(pravoEuPage, page, 'Predpisy')}</Text>
                  </Col>
                  {!isSmallScreen && (
                  <Col width="full" widthDesktop="two-thirds">
                    <PravoEuListSort />
                  </Col>
                  )}
                </Row>
                {pravoEuPage && (
                  <div className="pravo-eu-list">
                    <PravoEuListComponent
                      isSelectedFilter={isFilterEmpty(filter)}
                      pravoEuPage={pravoEuPage}
                    />
                    <Row className="pravo-eu-list-pagination-row">
                      <Col>
                        <Pagination
                          recordsPerPage={perPageDataArray}
                          hideTotal
                          number={page}
                          size={pravoEuPage.size}
                          totalElements={pravoEuPage.totalElements}
                          perPageValue={perPageValue}
                          onRecordsPerPageChange={onRecordsPerPageChange}
                        />
                      </Col>
                    </Row>
                  </div>
                )}
              </>
            </Loader>
          </Col>
        </Row>
      )}
    </div>
  );
};

function isFilterEmpty(filter: PravoEuFilter): boolean {
  return isStringEmpty(filter.hladanyVyraz)
    || isStringEmpty(filter.celex)
    || isNumberEmpty(filter.rokUverejnenia)
    || isStringEmpty(filter.eurovoc)
    || isCiselnikPolozkaEmpty(filter.type)
    || isCiselnikPolozkaEmpty(filter.autor)
    || isStringEmpty(filter.zmluva)
    || isDateEmpty(filter.datumUverejneniaOd)
    || isDateEmpty(filter.datumUverejneniaDo)
    || isDateEmpty(filter.datumSchvaleniaOd)
    || isDateEmpty(filter.datumSchvaleniaDo)
    || isDateEmpty(filter.datumUcinnostiOdOd)
    || isDateEmpty(filter.datumUcinnostiOdDo)
    || isDateEmpty(filter.datumUcinnostiDoOd)
    || isDateEmpty(filter.datumUcinnostiDoDo)
    || isDateEmpty(filter.datumUzavierkyOd)
    || isDateEmpty(filter.datumUzavierkyDo)
    || isDateEmpty(filter.datumTranspozicieOd)
    || isDateEmpty(filter.datumTranspozicieDo);
  //
}

function isStringEmpty(input: string): boolean {
  return input === undefined || input === '';
}

function isNumberEmpty(input: number): boolean {
  return input === undefined;
}

function isDateEmpty(input: Date): boolean {
  return input === undefined;
}

function isBooleanEmpty(input: boolean): boolean {
  return input === undefined;
}

function isCiselnikPolozkaEmpty(input: CiselnikPolozka): boolean {
  if (!input) return true;

  return isStringEmpty(input.uuid)
    || isStringEmpty(input.nazov)
    || isStringEmpty(input.kod)
    || isBooleanEmpty(input.platny)
    || isBooleanEmpty(input.legislativny)
    || isBooleanEmpty(input.existujeFormular)
    || isBooleanEmpty(input.generovanySystemom)
    || isBooleanEmpty(input.povolitZadavanieFormularom);
}

export default PravoEuList;
